import { useState, useEffect } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import TimeWindow from 'services/timeWindow'
import Dashboard from 'services/Dashboard'
import EarningsAndExpenses from './EarningsAndExpenses'
import ProfitPercentage from './ProfitPercentage'
import EarningsByPlan from './EarningsByPlan'
import AverageTickets from './AverageTickets'
import MembersByFrequency from './MembersByFrequency'
import Select from 'components/Inputs/Select'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { DateTime } from 'luxon'

import {
  DashboardContainer,
  LoadingContainer,
  FilterContainer,
  InfoText
} from '../styles'

export default function Earnings () {
  const now = DateTime.now ().setLocale ('pt-BR')
  const [month, setMonth] = useState ({
    label: now.toFormat ('MMMM'),
    value: now.month
  })
  const [year, setYear] = useState ({
    label: now.toFormat ('yyyy'),
    value: now.year
  })
  const [loading, setLoading] = useState (true)
  const periodFilterOptions = Dashboard.PERIOD_FILTERS
  const [periodFilter, setPeriodFilter] = useState (periodFilterOptions[0])
  const [earningsAndExpenses, setEarningsAndExpenses] = useState (null)
  const [profitPercentage, setProfitPercentage] = useState (null)
  const [earningsByPlan, setEarningsByPlan] = useState (null)
  const [averageTickets, setAverageTickets] = useState (null)
  const [moreFrequentMembers, setMoreFrequentMembers] = useState (null)
  const [lessFrequentMembers, setLessFrequentMembers] = useState (null)
  const token = AuthService.getToken ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    getData ()
  }, [month, year, periodFilter])

  const getData = async () => {
    try {
      setLoading (true)
      const selectedDate = DateTime.local (year.value, month.value)
      const startDate = selectedDate.minus ({ months: periodFilter.value }).startOf ('month').toISODate ()
      const endDate = selectedDate.endOf ('month').toISODate ()
      const filters = `start_date=${startDate}&end_date=${endDate}`

      const [
        { data: earningsAndExpenses },
        { data: profitPercentage },
        { data: earningsByPlan },
        { data: averageTickets },
        { data: moreFrequentMembers },
        { data: lessFrequentMembers }
      ] = await Promise.all ([
        cutileiApi.get (`/admin/dashboard/earnings_and_expenses?${filters}`, requestConfig),
        cutileiApi.get (`/admin/dashboard/profit_percentage?${filters}`, requestConfig),
        cutileiApi.get (`/admin/dashboard/earnings_by_plan?${filters}`, requestConfig),
        cutileiApi.get (`/admin/dashboard/avg_tickets?${filters}`, requestConfig),
        cutileiApi.get (`/admin/dashboard/members_by_frequency?${filters}&order=desc`, requestConfig),
        cutileiApi.get (`/admin/dashboard/members_by_frequency?${filters}&order=asc`, requestConfig)
      ])

      setEarningsAndExpenses (earningsAndExpenses)
      setEarningsByPlan (earningsByPlan)
      setProfitPercentage (profitPercentage)
      setAverageTickets (averageTickets)
      setMoreFrequentMembers (moreFrequentMembers)
      setLessFrequentMembers (lessFrequentMembers)
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  return (
    <>
      <FilterContainer style={{width: 540, marginTop: 0, marginBottom: 20}}>
        <Select
          options={TimeWindow.getMonths ()}
          value={month}
          onChange={(_, value) => setMonth (value)}
        />
        <Select
          options={TimeWindow.getYears ('2021', now.year.toString ())}
          value={year}
          onChange={(_, value) => setYear (value)}
        />
        <Select
          placeholder='Período...'
          value={periodFilter}
          options={periodFilterOptions}
          onChange={(_, option) => setPeriodFilter (option)}
        />
      </FilterContainer>
      {loading ? (
        <LoadingContainer>
          <Loading/>
          <InfoText>Gerando gráficos...</InfoText>
        </LoadingContainer>
      ) : (
        <DashboardContainer>
          <EarningsAndExpenses data={earningsAndExpenses}/>
          <ProfitPercentage data={profitPercentage}/>
          <AverageTickets data={averageTickets}/>
          <EarningsByPlan data={earningsByPlan} month={month.value} year={year.value}/>
          <MembersByFrequency data={moreFrequentMembers} order='desc' month={month.value} year={year.value}/>
          <MembersByFrequency data={lessFrequentMembers} order='asc' month={month.value} year={year.value}/>
        </DashboardContainer>
      )}
    </>
  )
}
